/**
 * =============================================================================
 * ZARGES QUICK VIEW
 * =============================================================================
 * Opens a modal preview when a Quick View button is clicked. Populates the
 * modal from data attributes, then fetches /products/{handle}.js to get the
 * canonical product state from Shopify (availability + tags). Sets the
 * Add to Cart button to one of three states based on truth:
 *
 *   1. CALL TO ORDER  — products tagged 'call-for-ordering-details';
 *                       button triggers tel:+18883576285
 *   2. SOLD OUT       — disabled gray button, no add-to-cart
 *   3. ADD TO CART    — default cyan button, AJAX add-to-cart
 *
 * Source of truth for state is /products/{handle}.js, which Shopify
 * guarantees matches the PDP exactly. The Liquid path also ships the same
 * info via data attributes for instant render before fetch resolves.
 * =============================================================================
 */

(function () {
  'use strict';

  var backdrop = null;
  var lastFocused = null;
  var productCache = {}; // handle → product json, avoid refetching

  function init() {
    backdrop = document.getElementById('zargesQuickViewBackdrop');
    if (!backdrop) return;

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-zarges-quickview]');
      if (trigger) {
        e.preventDefault();
        openModal(trigger);
        return;
      }
      if (e.target.closest('[data-zarges-close]')) {
        closeModal();
        return;
      }
      if (e.target === backdrop) {
        closeModal();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && backdrop.classList.contains('is-open')) {
        closeModal();
      }
    });

    var form = document.getElementById('zargesQuickViewAddForm');
    if (form) {
      form.addEventListener('submit', handleAddToCart);
    }
  }

  function openModal(trigger) {
    var data = trigger.dataset;

    setText('zargesQuickViewTitle', data.productTitle || '');
    setText('zargesQuickViewPrice', data.productPrice || '');
    setText('zargesQuickViewDesc', data.description || '');

    var img = document.getElementById('zargesQuickViewImage');
    if (img && data.productImage) {
      img.src = data.productImage;
      img.alt = data.productTitle || '';
    }

    var detailsLink = document.getElementById('zargesQuickViewDetailsLink');
    if (detailsLink) detailsLink.href = data.productUrl || '#';

    var variantInput = document.getElementById('zargesQuickViewVariantId');
    if (variantInput) variantInput.value = data.productVariantId || '';

    var specs = document.getElementById('zargesQuickViewSpecs');
    if (specs) {
      specs.innerHTML = '';
      appendSpec(specs, 'Exterior', data.exterior);
      appendSpec(specs, 'Interior', data.interior);
      appendSpec(specs, 'Volume', data.volume);
      appendSpec(specs, 'Weight (empty)', data.weight);
    }

    // Optimistic render from data attributes (Liquid path provides these;
    // USF path leaves them blank). Then fetch canonical state and update.
    setButtonState({
      available: data.productAvailable !== 'false',
      callToOrder: data.productCallToOrder === 'true',
      loading: !!data.productHandle && !productCache[data.productHandle]
    });

    // Fetch canonical product data if we have a handle. This is the source
    // of truth — it overrides whatever the data attribute said.
    if (data.productHandle) {
      fetchProductState(data.productHandle).then(function (state) {
        // Only apply if the modal is still showing this product
        if (variantInput && state.variantId &&
            (!variantInput.value || variantInput.value === data.productVariantId)) {
          variantInput.value = state.variantId;
        }
        setButtonState({
          available: state.available,
          callToOrder: state.callToOrder,
          loading: false
        });
      }).catch(function () {
        // Network error — clear loading state, keep whatever optimistic state
        setButtonState({
          available: data.productAvailable !== 'false',
          callToOrder: data.productCallToOrder === 'true',
          loading: false
        });
      });
    }

    lastFocused = document.activeElement;
    backdrop.classList.add('is-open');
    backdrop.setAttribute('aria-hidden', 'false');
    document.body.classList.add('zarges-modal-open');

    var closeBtn = backdrop.querySelector('[data-zarges-close]');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 50);
  }

  // Fetch /products/{handle}.js — Shopify's canonical product JSON. Cached.
  function fetchProductState(handle) {
    if (productCache[handle]) {
      return Promise.resolve(productCache[handle]);
    }
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Product fetch failed');
        return r.json();
      })
      .then(function (p) {
        var tags = Array.isArray(p.tags) ? p.tags : (typeof p.tags === 'string' ? p.tags.split(',') : []);
        var callToOrder = false;
        // Check the product template_suffix (some Shopify JSON responses
        // include it; safe to check optionally)
        if (p.template_suffix === 'call-for-ordering-details') {
          callToOrder = true;
        }
        // Also check for the matching tag
        if (!callToOrder) {
          for (var i = 0; i < tags.length; i++) {
            var t = String(tags[i]).trim().toLowerCase();
            if (t === 'call-for-ordering-details') { callToOrder = true; break; }
          }
        }
        // Find the first available variant for the cart-add ID
        var firstAvailableVariant = null;
        if (p.variants && p.variants.length) {
          for (var j = 0; j < p.variants.length; j++) {
            if (p.variants[j].available) {
              firstAvailableVariant = p.variants[j];
              break;
            }
          }
        }
        var state = {
          available: !!p.available,
          callToOrder: callToOrder,
          variantId: firstAvailableVariant ? firstAvailableVariant.id :
                     (p.variants && p.variants[0] ? p.variants[0].id : null)
        };
        productCache[handle] = state;
        return state;
      });
  }

  // Set button to one of: loading, call-to-order, sold-out, add-to-cart
  function setButtonState(opts) {
    var btn = document.getElementById('zargesQuickViewAddBtn');
    if (!btn) return;

    btn.disabled = false;
    btn.classList.remove('is-sold-out', 'is-call-to-order', 'is-loading');
    btn.removeAttribute('aria-disabled');

    if (opts.loading) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.innerHTML = 'Checking…';
      return;
    }

    if (opts.callToOrder) {
      btn.classList.add('is-call-to-order');
      btn.innerHTML =
        '<span class="zarges-btn-icon" aria-hidden="true">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">' +
            '<path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.1.37 2.29.57 3.58.57a1 1 0 011 1v3.5a1 1 0 01-1 1C11.3 19.82 4.18 12.7 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.29.2 2.48.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z"/>' +
          '</svg>' +
        '</span>' +
        '<span>Call For Ordering Details</span>';
      btn.setAttribute('aria-label', 'Call for ordering details at 888-357-6285');
      return;
    }

    if (!opts.available) {
      btn.classList.add('is-sold-out');
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.textContent = 'Sold Out';
      return;
    }

    btn.textContent = 'Add to Cart';
  }

  function handleAddToCart(e) {
    e.preventDefault();
    var form = e.currentTarget;
    var btn = form.querySelector('button[type="submit"]');

    // Call-to-order: dial instead of cart add
    if (btn && btn.classList.contains('is-call-to-order')) {
      window.location.href = 'tel:+18883576285';
      return;
    }

    // Sold-out or loading: hard block
    if (btn && (btn.disabled || btn.classList.contains('is-sold-out') || btn.classList.contains('is-loading'))) {
      return;
    }

    var variantInput = document.getElementById('zargesQuickViewVariantId');
    var variantId = variantInput ? variantInput.value : '';
    if (!variantId) {
      var detailsLink = document.getElementById('zargesQuickViewDetailsLink');
      if (detailsLink && detailsLink.href && detailsLink.href !== '#') {
        window.location.href = detailsLink.href;
      }
      return;
    }

    var originalLabel = btn ? btn.textContent : '';
    if (btn) {
      btn.textContent = 'Adding…';
      btn.disabled = true;
    }

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || err.message || 'Add to cart failed');
          });
        }
        return response.json();
      })
      .then(function () {
        try {
          if (window.theme && window.theme.pubsub && window.theme.pubsub.publish && window.theme.pubsub.PUB_SUB_EVENTS) {
            window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'zarges-quickview',
              productVariantId: variantId
            });
          }
        } catch (err) {}

        try {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.show === 'function') drawer.show(form);
        } catch (err) {}

        if (btn) {
          btn.textContent = 'Added ✓';
          setTimeout(function () {
            closeModal();
            btn.textContent = originalLabel;
            btn.disabled = false;
          }, 800);
        } else {
          closeModal();
        }
      })
      .catch(function (err) {
        console.error('[Zarges Quick View] Add to cart error:', err);
        if (btn) {
          btn.textContent = 'Try again';
          btn.disabled = false;
          setTimeout(function () {
            btn.textContent = originalLabel;
          }, 2000);
        }
      });
  }

  function closeModal() {
    if (!backdrop) return;
    backdrop.classList.remove('is-open');
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('zarges-modal-open');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  function setText(id, value) {
    var el = document.getElementById(id);
    if (el) el.textContent = value;
  }

  function appendSpec(dl, label, value) {
    if (!value) return;
    var dt = document.createElement('dt');
    dt.textContent = label;
    var dd = document.createElement('dd');
    dd.textContent = value;
    dl.appendChild(dt);
    dl.appendChild(dd);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
